import { useCallback, useEffect, useRef, useState } from 'react'
import type { TermsView } from '@assessments/shared'
import { getTerms } from './api.js'

export interface UseTerms {
  data: TermsView | null
  loading: boolean
  error: string | null
  reload: () => void
}

// Uses a monotonic request id so a superseded response (e.g. after a reload
// following a version change) can't apply out of order.
export function useTerms(organizationSurveyId: string): UseTerms {
  const [data, setData] = useState<TermsView | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const requestId = useRef(0)

  const load = useCallback(() => {
    const id = ++requestId.current
    setLoading(true)
    setError(null)
    void (async () => {
      try {
        const view = await getTerms(organizationSurveyId)
        if (id === requestId.current) setData(view)
      } catch (err: unknown) {
        if (id === requestId.current) setError(err instanceof Error ? err.message : String(err))
      } finally {
        if (id === requestId.current) setLoading(false)
      }
    })()
    return () => {
      requestId.current++
    }
  }, [organizationSurveyId])

  useEffect(() => {
    const cancel = load()
    function onFocus() {
      load()
    }
    window.addEventListener('focus', onFocus)
    return () => {
      cancel()
      window.removeEventListener('focus', onFocus)
    }
  }, [load])

  return { data, loading, error, reload: load }
}
